import styled from '@emotion/styled';
import { captureException } from 'src/shared/helpers/captureException';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Button, ButtonGroup, pxToRem, StarRatingPicker } from '@modernhealth/vital-components';

import { appClient } from 'src/config/apollo';
import { PageImage } from 'src/modules/newPrograms/components/PageImage';
import { getUserInformation } from 'src/modules/user/selectors/userSelectors';
import successImg from 'src/public/img/tada2.svg';
import { color, font } from 'src/shared/styles/variables';

import { useReviewProgramMutation } from 'src/modules/newPrograms/documents/reviewProgramMutation.generated';
import { useProgramContext } from '../contexts/programContext';
import { useHistory } from 'react-router';

const Container = styled.div({
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
});

const Header = styled.h1({
    marginTop: 30,
    marginBottom: 10,
    ...font.black(font.size.extraLarge),
});

const CongratsStepHeader = styled(Header)({
    marginTop: 100,
    marginBottom: 20,
});

const SubHeader = styled.h5({
    marginBottom: 5,
    color: color.blueBayoux,
    ...font.book(font.size.medium),
});

const HowWasThisProgramText = styled.p({
    color: color.ebonyClay,
    marginTop: 16,
    marginBottom: 15,
    ...font.heavy(font.size.medium),
});

const IntroButtonWrapper = styled.div({
    maxWidth: 241,
    width: '100%',
});

const AnswersList = styled.ul({
    padding: 0,
    marginTop: 35,
    maxWidth: 495,
    width: '100%',
    flexWrap: 'wrap',
    display: 'flex',
    justifyContent: 'space-between',
});

interface AnswerElementProps {
    selected?: boolean;
}

const AnswerElement = styled.li<AnswerElementProps>(props => ({
    width: '48.5%',
    textAlign: 'center',
    marginBottom: 10,
    border: `2px solid ${props.selected ? color.royalBlue : color.catskillWhite}`,
    padding: '13px 0 10px',
    borderRadius: 6,
    cursor: 'pointer',
    color: color.ebonyClay,
    transition: '.3s ease-in-out all',
    ...font[props.selected ? 'heavy' : 'book'](font.size.basePlus),
}));

enum RateProgramStep {
    Intro = 'RateProgramStep.IntroStep',
    Question = 'RateProgramStep.QuestionStep',
    Success = 'RateProgramStep.SuccessStep',
}

export const whatDidYouLikeAnswersOptions = [
    { translationKey: 'positive_answer.1', value: 'Helpful' },
    { translationKey: 'positive_answer.2', value: 'Interesting' },
    { translationKey: 'positive_answer.3', value: 'Relevant' },
    { translationKey: 'positive_answer.4', value: 'Informative' },
    { translationKey: 'positive_answer.5', value: 'Practical' },
    { translationKey: 'positive_answer.6', value: 'Insightful' },
];
export const whatDidYouDislikeAnswersOptions = [
    { translationKey: 'negative_answer.1', value: 'Irrelevant' },
    { translationKey: 'negative_answer.2', value: 'Unclear' },
    { translationKey: 'negative_answer.3', value: 'I wanted something new' },
    { translationKey: 'negative_answer.4', value: 'Impractical' },
    { translationKey: 'negative_answer.5', value: 'Too difficult' },
    { translationKey: 'negative_answer.6', value: 'Unhelpful' },
];

export const ProgramRatingPage: React.FC = () => {
    const history = useHistory();
    const exit = () => history.go(-2);
    const { program } = useProgramContext();
    const { t } = useTranslation();

    const user = useSelector(getUserInformation);

    const [reviewProgram, { loading }] = useReviewProgramMutation({ client: appClient });

    const [activeStep, setActiveStep] = useState(RateProgramStep.Intro);
    const [starsRating, setStarsRating] = useState(0);
    const [whatDidYouLikeAnswers, setWhatDidYouLikeAnswers] = useState<string[]>([]);
    const [whatDidYouDislikeAnswers, setWhatDidYouDislikeAnswers] = useState<string[]>([]);

    const answersOptions =
        starsRating > 3 ? whatDidYouLikeAnswersOptions : whatDidYouDislikeAnswersOptions;
    const answers = starsRating > 3 ? whatDidYouLikeAnswers : whatDidYouDislikeAnswers;

    const handleSubmit = async () => {
        try {
            await reviewProgram({
                variables: {
                    programUuid: program.uuid,
                    rating: starsRating,
                    dislikedReasons: starsRating > 3 ? [] : whatDidYouDislikeAnswers,
                    likedReasons: starsRating > 3 ? whatDidYouLikeAnswers : [],
                },
            });
        } catch (error) {
            captureException(error);
        } finally {
            setTimeout(() => {
                exit();
            }, 3000);

            setActiveStep(RateProgramStep.Success);
        }
    };

    const handleAnswerClick = (newAnswer: string) => {
        const setAnswersFunc =
            starsRating > 3 ? setWhatDidYouLikeAnswers : setWhatDidYouDislikeAnswers;

        const newAnswers = answers.includes(newAnswer)
            ? answers.filter(answer => answer !== newAnswer)
            : [...answers, newAnswer];
        setAnswersFunc(newAnswers);
    };

    const handleRatingChange = (newRating: number) => {
        setStarsRating(oldRating => {
            if (oldRating === 0) {
                setActiveStep(RateProgramStep.Question);
            }

            return newRating;
        });
    };

    return (
        <Container>
            {activeStep === RateProgramStep.Intro ? (
                <>
                    <PageImage src={successImg} width={220} height={180} />
                    <Header>{t('programs:program.congratulations.nice_work')}</Header>
                    <SubHeader>
                        {t('programs:program.rating.program_completed', {
                            programName: program.name,
                        })}
                    </SubHeader>

                    <HowWasThisProgramText>
                        {t('programs:program.rating.how_was_question')}
                    </HowWasThisProgramText>

                    <StarRatingPicker
                        defaultRating={starsRating}
                        onRatingChange={handleRatingChange}
                    />

                    <IntroButtonWrapper>
                        <Button
                            onClick={exit}
                            style={{ marginTop: pxToRem(50) }}
                            variant="secondary"
                            fluid
                        >
                            {t('common.skip')}
                        </Button>
                    </IntroButtonWrapper>
                </>
            ) : activeStep === RateProgramStep.Question ? (
                <>
                    <Header>
                        {t(
                            `programs:program.rating.${
                                starsRating > 3 ? 'positive_question' : 'negative_question'
                            }`,
                        )}
                    </Header>
                    <StarRatingPicker
                        defaultRating={starsRating}
                        onRatingChange={handleRatingChange}
                    />
                    <AnswersList>
                        {answersOptions.map(({ translationKey, value }) => (
                            <AnswerElement
                                key={value}
                                onClick={() => handleAnswerClick(value)}
                                selected={answers.includes(value)}
                            >
                                {t(`programs:program.rating.${translationKey}`)}
                            </AnswerElement>
                        ))}
                    </AnswersList>
                    <ButtonGroup css={{ flex: 0 }}>
                        <Button onClick={() => exit()} variant="secondary">
                            {t('common.skip')}
                        </Button>
                        <Button
                            onClick={handleSubmit}
                            busy={loading}
                            disabled={answers.length === 0}
                        >
                            {t('common.submit')}
                        </Button>
                    </ButtonGroup>
                </>
            ) : (
                <>
                    <PageImage src={successImg} width={220} height={180} />
                    <CongratsStepHeader>
                        {t('programs:program.rating.thanks_heading', { firstName: user.firstName })}
                    </CongratsStepHeader>
                    <SubHeader> {t('programs:program.rating.thanks_subheading')}</SubHeader>
                </>
            )}
        </Container>
    );
};
